import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { PrincipalDocumentosLista } from "../../../auditoria/domain/models/documentos_lista";
import { useAuditoresPagination } from "../../../auditoria/presentation/hooks/Auditoria_auditores/pagination";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { DashboardEntregaObras } from "../../domain/models/dashboard_entrega_obras";
import { initialEntregaObrasFiltersValues, useEntregaObrasListaFilters } from "../hooks/entrega_obra_lista/filters";
import { useEntregaObrasListaPagination } from "../hooks/entrega_obra_lista/pagination";
import { initializePesquisaClienteListaState, usePesquisaClienteListaFilters } from "../hooks/PesquisaCliente_lista/filters";
import { usePesquisaClienteListaPagination } from "../hooks/PesquisaCliente_lista/pagination";
import { initialAssistenciaTecnicaListaFiltersState, useAssistenciaTecnicaListaFilters } from "../hooks/AssistenciaTecnica_lista/filters";
import { useAssistenciaTecnicaListaPagination } from "../hooks/AssistenciaTecnica_lista/pagination";

import CommonConstants from "../../../../common/constants";

/**
 * LoadPrincipalListaFuncState
 */

type LoadPrincipalListaFuncState = {
  IdArquivoModulo?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  pesquisa?: string,
};

/**
 * usePrincipalEntregaObrasViewModel
 */

export const usePrincipalEntregaObrasViewModel = () => {
  const {
    commons: {
      getAllListagemDocUseCase,
      getGedArquivoDataUseCase,
    },
    entregaObras: {
      getDashboardEntregaObrasInformationUseCase,
    },
  } = useDependencyInjection();

  const { pushErrorNotification } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [dashboardEntregaObrasInformation, setDashboardEntregaObrasInformation] = useState<DashboardEntregaObras>();

  const [docLista, setDocLista] = useState<PrincipalDocumentosLista[]>([]);

  const pagination = useAuditoresPagination();

  /**
   * Funcoes de carregamento
   */

  const loadDashboardEntregaObrasInformation = useCallback(async () => {
    try {
      const result = await getDashboardEntregaObrasInformationUseCase.getDashboardEntregaObrasInformation();
      setDashboardEntregaObrasInformation(result);
    } catch (e: unknown) {
      setDashboardEntregaObrasInformation(undefined);
      throw new Error("Falha ao buscar informações do dashboard!");
    }
  }, [
    getDashboardEntregaObrasInformationUseCase,
  ]);

  const loadDocListaState = useCallback(async (funcState?: LoadPrincipalListaFuncState) => {
    try {
      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
      await getAllListagemDocUseCase.getAllListagemDoc(
        String(CommonConstants.moduloDocumentos.EntregaObras),
        filteredPage,
        filteredRowsPerPage
      );
      setDocLista(getAllListagemDocUseCase.items);
      pagination.setRowsPerPage(getAllListagemDocUseCase.pageSize);
      pagination.setTotalItems(getAllListagemDocUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error("Falha ao buscar documentos!");
    }
  }, [
    getAllListagemDocUseCase,
    pagination,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadDashboardEntregaObrasInformation();
      await loadDocListaState();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao carregar os dados. Tente novamente.')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(undefined);
      await loadDashboardEntregaObrasInformation();
      await loadDocListaState();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao recarregar os dados. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  }, [
    loadDashboardEntregaObrasInformation,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleCardClick = useCallback((cardType: string) => {
    switch (cardType) {
      case 'obrasEntregues':
        useEntregaObrasListaFilters.setState({ ...initialEntregaObrasFiltersValues, status: 1 });
        useEntregaObrasListaPagination.setState({ page: 1 });
        backOrNavigate('/EntregaObras_lista');
        break;
      case 'satisfacaoClienteEntregaObras':
        usePesquisaClienteListaFilters.setState({ ...initializePesquisaClienteListaState });
        usePesquisaClienteListaPagination.setState({ page: 1 });
        backOrNavigate('/PesquisaCliente_lista');
        break;
      case 'satisfacaoClienteAssistenciaTecnica':
        useAssistenciaTecnicaListaFilters.setState({ ...initialAssistenciaTecnicaListaFiltersState });
        useAssistenciaTecnicaListaPagination.setState({ page: 1 });
        backOrNavigate('/AssistenciaTecnica_lista');
        break;
      case 'assistenciasTecnicasEmAberto':
        useAssistenciaTecnicaListaFilters.setState({ ...initialAssistenciaTecnicaListaFiltersState, resolvido: 5 });
        useAssistenciaTecnicaListaPagination.setState({ page: 1 });
        backOrNavigate('/AssistenciaTecnica_lista');
        break;
    }
  }, [
    backOrNavigate,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadDocListaState({ page: page + 1, });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);

      await loadDocListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleDownloadFile = useCallback(async (IdArquivoModulo: number, NomeArquivo: string) => {
    if (IdArquivoModulo > 0) {
      try {
        setLoading(true);
        await getGedArquivoDataUseCase
          .getGedArquivoData(IdArquivoModulo, NomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    getGedArquivoDataUseCase,
    pushErrorNotification,
  ]);

  const handleRegistradaClick = useCallback((idCentroCusto: number) => {
    useAssistenciaTecnicaListaFilters.setState({ ...initialAssistenciaTecnicaListaFiltersState, centroCustos: [idCentroCusto] });
    useAssistenciaTecnicaListaPagination.setState({ page: 1 });
    backOrNavigate('/AssistenciaTecnica_lista');
  }, [
    backOrNavigate,
  ]);

  const handleEmAnaliseClick = useCallback((idCentroCusto: number) => {
    useAssistenciaTecnicaListaFilters.setState({ ...initialAssistenciaTecnicaListaFiltersState, centroCustos: [idCentroCusto], resolvido: 5 });
    useAssistenciaTecnicaListaPagination.setState({ page: 1 });
    backOrNavigate('/AssistenciaTecnica_lista');
  }, [
    backOrNavigate,
  ]);

  const handleResolvidaClick = useCallback((idCentroCusto: number) => {
    useAssistenciaTecnicaListaFilters.setState({ ...initialAssistenciaTecnicaListaFiltersState, centroCustos: [idCentroCusto], resolvido: 1 });
    useAssistenciaTecnicaListaPagination.setState({ page: 1 });
    backOrNavigate('/AssistenciaTecnica_lista');
  }, [
    backOrNavigate,
  ]);

  return {
    loading,
    errorMessage,
    dashboardEntregaObrasInformation: dashboardEntregaObrasInformation ?? null,
    docLista,
    pagination,
    handleReload,
    handleCardClick,
    handlePageChange,
    handleRowsPerPageChange,
    handleDownloadFile,
    handleRegistradaClick,
    handleEmAnaliseClick,
    handleResolvidaClick,
  };
}